import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Eye, Search, ArrowUpDown, Calendar, MapPin, Activity, Image, Mail, Ban, Download, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { EnhancedTable } from '@/components/admin/EnhancedTable';
import { BulkActions } from '@/components/admin/BulkActions';
import { SplitPane } from '@/components/admin/SplitPane';
import { toast } from '@/hooks/use-toast';

interface UserData {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  created_at: string;
  last_login?: string;
  status: string;
  total_points?: number;
  last_checkin?: {
    timestamp: string;
    location: string;
  };
  total_checkins?: number;
}

interface CheckinData {
  id: string;
  garita: string;
  mode: string;
  lane_type: string;
  created_at: string;
  photo_url?: string;
}

interface UserDetail {
  user: UserData;
  checkins: CheckinData[];
  photos: string[];
}

type SortField = 'full_name' | 'email' | 'created_at' | 'total_points' | 'last_login' | 'total_checkins';
type SortDirection = 'asc' | 'desc';

interface UserManagementProps {
  users: UserData[];
  searchTerm: string;
  statusFilter: string;
  pointsAdjustment: string;
  pointsReason: string;
  selectedUserForPoints: string | null;
  isSpanish: boolean;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onViewUser: (userId: string) => void;
  onSetSelectedUserForPoints: (userId: string | null) => void;
  onPointsAdjustmentChange: (value: string) => void;
  onPointsReasonChange: (value: string) => void;
  onAdjustUserPoints: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({
  users: initialUsers,
  searchTerm,
  statusFilter,
  pointsAdjustment,
  pointsReason,
  selectedUserForPoints,
  isSpanish,
  onSearchChange,
  onStatusFilterChange,
  onViewUser,
  onSetSelectedUserForPoints,
  onPointsAdjustmentChange,
  onPointsReasonChange,
  onAdjustUserPoints
}) => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [locationFilter, setLocationFilter] = useState('all');
  const [pointsRangeFilter, setPointsRangeFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);
  const [showSplitView, setShowSplitView] = useState(false);

  // Fetch enhanced user data
  useEffect(() => {
    fetchUsersWithDetails();
  }, []);

  const fetchUsersWithDetails = async () => {
    try {
      // Fetch users
      const { data: usersData } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      // Fetch points transactions separately
      const { data: pointsData } = await supabase
        .from('points_transactions')
        .select('user_id, points_amount');

      // Fetch checkin stats for each user
      const { data: checkinStats } = await supabase
        .from('checkins')
        .select('user_id, garita, created_at')
        .order('created_at', { ascending: false });

      // Process the data
      const processedUsers = (usersData || []).map(user => {
        const userCheckins = (checkinStats || []).filter(c => c.user_id === user.id);
        const userPointsTransactions = (pointsData || []).filter(p => p.user_id === user.id);
        const totalPoints = userPointsTransactions
          .reduce((sum: number, transaction: any) => sum + (transaction.points_amount || 0), 0);
        
        const lastCheckin = userCheckins[0];
        const totalCheckins = userCheckins.length;

        return {
          ...user,
          total_points: totalPoints,
          total_checkins: totalCheckins,
          last_checkin: lastCheckin ? {
            timestamp: lastCheckin.created_at,
            location: lastCheckin.garita
          } : undefined
        };
      });

      setUsers(processedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId: string) => {
    setUserDetailLoading(true);
    try {
      // Fetch user data
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      // Fetch user's checkins
      const { data: checkins } = await supabase
        .from('checkins')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Extract photos
      const photos = (checkins || [])
        .filter(c => c.photo_url)
        .map(c => c.photo_url);

      setSelectedUser({
        user: userData,
        checkins: checkins || [],
        photos: photos || []
      });
    } catch (error) {
      console.error('Error fetching user detail:', error);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getActiveStatus = (user: UserData) => {
    if (!user.last_login) return 'Never logged in';
    
    const daysSinceLogin = Math.floor(
      (Date.now() - new Date(user.last_login).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSinceLogin === 0) return 'Active today';
    if (daysSinceLogin === 1) return 'Last seen yesterday';
    if (daysSinceLogin <= 7) return `Last seen ${daysSinceLogin} days ago`;
    return 'Inactive';
  };

  // Enhanced search and filter
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      // Search filter (name, email, phone)
      const searchMatch = !searchTerm || 
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone_number && user.phone_number.includes(searchTerm));

      // Status filter
      const statusMatch = statusFilter === 'all' || user.status === statusFilter;

      // Location filter
      const locationMatch = locationFilter === 'all' || 
        (user.last_checkin && user.last_checkin.location === locationFilter);

      // Points range filter
      const pointsMatch = (() => {
        const points = user.total_points || 0;
        switch (pointsRangeFilter) {
          case 'low': return points < 100;
          case 'medium': return points >= 100 && points < 500;
          case 'high': return points >= 500;
          default: return true;
        }
      })();

      // Date range filter
      const dateMatch = (() => {
        const createdDate = new Date(user.created_at);
        const now = new Date();
        switch (dateRangeFilter) {
          case 'today': 
            return createdDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return createdDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return createdDate >= monthAgo;
          default: return true;
        }
      })();

      return searchMatch && statusMatch && locationMatch && pointsMatch && dateMatch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'full_name':
          aValue = a.full_name;
          bValue = b.full_name;
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'total_points':
          aValue = a.total_points || 0;
          bValue = b.total_points || 0;
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login) : new Date(0);
          bValue = b.last_login ? new Date(b.last_login) : new Date(0);
          break;
        case 'total_checkins':
          aValue = a.total_checkins || 0;
          bValue = b.total_checkins || 0;
          break;
        default:
          aValue = a.created_at;
          bValue = b.created_at;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, statusFilter, locationFilter, pointsRangeFilter, dateRangeFilter, sortField, sortDirection]);

  // Bulk actions
  const handleBulkEmail = () => {
    toast({
      title: isSpanish ? 'Enviando emails...' : 'Sending emails...',
      description: `${selectedUsers.length} ${isSpanish ? 'usuarios seleccionados' : 'users selected'}`,
    });
    // Implement bulk email logic
  };

  const handleBulkSuspend = () => {
    toast({
      title: isSpanish ? 'Suspendiendo usuarios...' : 'Suspending users...',
      description: `${selectedUsers.length} ${isSpanish ? 'usuarios' : 'users'}`,
      variant: 'destructive',
    });
    // Implement bulk suspend logic
  };

  const handleBulkExport = () => {
    const csvContent = [
      ['Name', 'Email', 'Points', 'Check-ins', 'Status'],
      ...selectedUsers.map(u => [
        u.full_name,
        u.email,
        u.total_points || 0,
        u.total_checkins || 0,
        u.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-export.csv';
    a.click();
    
    toast({
      title: isSpanish ? 'Exportación completa' : 'Export complete',
      description: `${selectedUsers.length} ${isSpanish ? 'usuarios exportados' : 'users exported'}`,
    });
  };

  const bulkActions = [
    {
      id: 'email',
      label: isSpanish ? 'Enviar Email' : 'Send Email',
      icon: Mail,
      action: handleBulkEmail,
    },
    {
      id: 'export',
      label: isSpanish ? 'Exportar' : 'Export',
      icon: Download,
      action: handleBulkExport,
    },
    {
      id: 'suspend',
      label: isSpanish ? 'Suspender' : 'Suspend',
      icon: Ban,
      variant: 'destructive' as const,
      action: handleBulkSuspend,
    },
  ];

  const tableColumns = [
    {
      id: 'full_name',
      label: isSpanish ? 'Nombre' : 'Name',
      accessor: 'full_name' as keyof UserData,
      minWidth: 150,
      width: 200,
    },
    {
      id: 'email',
      label: 'Email',
      accessor: 'email' as keyof UserData,
      minWidth: 180,
      width: 250,
    },
    {
      id: 'created_at',
      label: isSpanish ? 'Fecha Registro' : 'Registration Date',
      accessor: (row: UserData) => new Date(row.created_at).toLocaleDateString(),
      minWidth: 120,
      width: 150,
    },
    {
      id: 'last_checkin',
      label: isSpanish ? 'Último Check-In' : 'Last Check-In',
      accessor: (row: UserData) => row.last_checkin ? (
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-muted-foreground" />
          <span>{row.last_checkin.location}</span>
        </div>
      ) : (
        <span className="text-muted-foreground">{isSpanish ? 'Nunca' : 'Never'}</span>
      ),
      minWidth: 150,
      width: 180,
    },
    {
      id: 'total_checkins',
      label: isSpanish ? 'Total Check-Ins' : 'Total Check-Ins',
      accessor: (row: UserData) => (
        <Badge variant="outline">{row.total_checkins || 0}</Badge>
      ),
      minWidth: 100,
      width: 120,
    },
    {
      id: 'total_points',
      label: isSpanish ? 'Puntos' : 'Points',
      accessor: (row: UserData) => (
        <Badge variant="outline">{row.total_points || 0} pts</Badge>
      ),
      minWidth: 100,
      width: 120,
    },
    {
      id: 'status',
      label: isSpanish ? 'Estado Activo' : 'Active Status',
      accessor: (row: UserData) => (
        <div className="flex items-center gap-1">
          <Activity className="w-3 h-3 text-muted-foreground" />
          <span className="text-sm">{getActiveStatus(row)}</span>
        </div>
      ),
      minWidth: 140,
      width: 160,
    },
  ];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Search and Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder={isSpanish ? "Buscar por nombre, email o teléfono..." : "Search by name, email or phone..."}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger>
            <SelectValue placeholder={isSpanish ? "Estado" : "Status"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? "Todos" : "All"}</SelectItem>
            <SelectItem value="active">{isSpanish ? "Activos" : "Active"}</SelectItem>
            <SelectItem value="suspended">{isSpanish ? "Suspendidos" : "Suspended"}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger>
            <SelectValue placeholder={isSpanish ? "Ubicación" : "Location"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? "Todas las ubicaciones" : "All locations"}</SelectItem>
            <SelectItem value="San Ysidro">San Ysidro</SelectItem>
            <SelectItem value="Otay Mesa">Otay Mesa</SelectItem>
            <SelectItem value="Tecate">Tecate</SelectItem>
            <SelectItem value="Mexicali">Mexicali</SelectItem>
          </SelectContent>
        </Select>

        <Select value={pointsRangeFilter} onValueChange={setPointsRangeFilter}>
          <SelectTrigger>
            <SelectValue placeholder={isSpanish ? "Rango de puntos" : "Points range"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? "Todos los puntos" : "All points"}</SelectItem>
            <SelectItem value="low">{isSpanish ? "Menos de 100" : "Less than 100"}</SelectItem>
            <SelectItem value="medium">{isSpanish ? "100-500" : "100-500"}</SelectItem>
            <SelectItem value="high">{isSpanish ? "Más de 500" : "500+"}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-4">
        <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={isSpanish ? "Fecha de registro" : "Registration date"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isSpanish ? "Todas las fechas" : "All dates"}</SelectItem>
            <SelectItem value="today">{isSpanish ? "Hoy" : "Today"}</SelectItem>
            <SelectItem value="week">{isSpanish ? "Esta semana" : "This week"}</SelectItem>
            <SelectItem value="month">{isSpanish ? "Este mes" : "This month"}</SelectItem>
          </SelectContent>
        </Select>
        
        <div className="text-sm text-muted-foreground flex items-center">
          {isSpanish ? `Mostrando ${filteredAndSortedUsers.length} de ${users.length} usuarios` : 
                      `Showing ${filteredAndSortedUsers.length} of ${users.length} users`}
        </div>
      </div>

      {/* Enhanced Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{isSpanish ? 'Usuarios Registrados' : 'Registered Users'}</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSplitView(!showSplitView)}
            >
              {showSplitView ? (isSpanish ? 'Vista Simple' : 'Simple View') : (isSpanish ? 'Vista Dividida' : 'Split View')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActions
            selectedCount={selectedUsers.length}
            actions={bulkActions}
            onClearSelection={() => setSelectedUsers([])}
            isSpanish={isSpanish}
          />
          
          {showSplitView && selectedUser ? (
            <SplitPane
              left={
                <EnhancedTable
                  data={filteredAndSortedUsers}
                  columns={tableColumns}
                  onRowSelect={setSelectedUsers}
                  selectable
                  stickyHeader
                  onRowClick={(row) => fetchUserDetail(row.id)}
                  rowKey="id"
                />
              }
              right={
                <div className="p-6">
                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="grid grid-cols-1 gap-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">{isSpanish ? 'Información Personal' : 'Personal Information'}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div>
                            <span className="font-medium">{isSpanish ? 'Nombre:' : 'Name:'}</span>
                            <p>{selectedUser.user.full_name}</p>
                          </div>
                          <div>
                            <span className="font-medium">{isSpanish ? 'Email:' : 'Email:'}</span>
                            <p>{selectedUser.user.email}</p>
                          </div>
                          <div>
                            <span className="font-medium">{isSpanish ? 'Puntos Totales:' : 'Total Points:'}</span>
                            <Badge variant="outline" className="ml-2">
                              {selectedUser.user.total_points || 0} pts
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Check-In History */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">{isSpanish ? 'Historial de Check-Ins' : 'Check-In History'}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2">
                            {selectedUser.checkins.map((checkin) => (
                              <div key={checkin.id} className="flex items-center justify-between p-2 border rounded">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">{checkin.garita}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {checkin.mode} - {checkin.lane_type}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm">{new Date(checkin.created_at).toLocaleDateString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              }
            />
          ) : (
            <EnhancedTable
              data={filteredAndSortedUsers}
              columns={tableColumns}
              onRowSelect={setSelectedUsers}
              selectable
              stickyHeader
              onRowClick={(row) => fetchUserDetail(row.id)}
              rowKey="id"
            />
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {isSpanish ? 'Detalle del Usuario' : 'User Details'}
            </DialogTitle>
          </DialogHeader>
          
          {userDetailLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">{isSpanish ? 'Información Personal' : 'Personal Information'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <span className="font-medium">{isSpanish ? 'Nombre:' : 'Name:'}</span>
                      <p>{selectedUser.user.full_name}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Email:' : 'Email:'}</span>
                      <p>{selectedUser.user.email}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Teléfono:' : 'Phone:'}</span>
                      <p>{selectedUser.user.phone_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Puntos Totales:' : 'Total Points:'}</span>
                      <Badge variant="outline" className="ml-2">
                        {selectedUser.user.total_points || 0} pts
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">{isSpanish ? 'Estadísticas' : 'Statistics'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <span className="font-medium">{isSpanish ? 'Fecha de Registro:' : 'Registration Date:'}</span>
                      <p>{new Date(selectedUser.user.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Total Check-Ins:' : 'Total Check-Ins:'}</span>
                      <p>{selectedUser.checkins.length}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Fotos Subidas:' : 'Photos Submitted:'}</span>
                      <p>{selectedUser.photos.length}</p>
                    </div>
                    <div>
                      <span className="font-medium">{isSpanish ? 'Estado:' : 'Status:'}</span>
                      <Badge variant={selectedUser.user.status === 'active' ? 'default' : 'destructive'} className="ml-2">
                        {selectedUser.user.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Check-In History */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{isSpanish ? 'Historial de Check-Ins' : 'Check-In History'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {selectedUser.checkins.map((checkin) => (
                        <div key={checkin.id} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{checkin.garita}</p>
                              <p className="text-sm text-muted-foreground">
                                {checkin.mode} - {checkin.lane_type}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">{new Date(checkin.created_at).toLocaleDateString()}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(checkin.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Photos */}
              {selectedUser.photos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      {isSpanish ? 'Fotos Subidas' : 'Submitted Photos'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {selectedUser.photos.map((photo, index) => (
                        <div key={index} className="aspect-square">
                          <img
                            src={photo}
                            alt={`User photo ${index + 1}`}
                            className="w-full h-full object-cover rounded border"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;